import { Injectable, OnDestroy } from '@angular/core';

import { Observable, of, Subject } from 'rxjs';
import { JsonApiService } from './base/json.api.service';
import { HttpHandler, HttpHeaders, HttpParams } from '@angular/common/http';

// Store
import { Store } from '@ngrx/store';
import { AppState } from '../../../store/';

// Actions
import * as RouterActions from '../../../store/router/router.actions';
import { delay, map, takeUntil } from 'rxjs/operators';
import { sha1 } from '../../lib/helpers/utils';
import servicesConfig from './cacheableServices';

@Injectable()
export class ApiService extends JsonApiService implements OnDestroy {
    clienteCodigo: string;
    private cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
    private destroy$ = new Subject<void>();

    constructor(backend: HttpHandler, private store: Store<AppState>) {
        super(backend);
        this.store
            .select((state) => state.user.data.clienteCodigo)
            .pipe(takeUntil(this.destroy$))
            .subscribe((clienteCodigo) => {
                if (clienteCodigo) {
                    this.clienteCodigo = clienteCodigo;
                }
            });
    }

    /**
     * Environment configuration
     *
     * @type {*}
     */
    environment: any = null;

    /**
     * Api initialization
     *
     * @param {*} environment
     */
    init(environment: any) {
        this.environment = environment;
        this.checkurl();
    }

    /**
     * Check api urls to determine endpoint
     * @param {number = 0} index Initial index
     */
    checkurl(index: number = 0) {
        if (this.environment.urls.length > 0) {
            const url = this.environment.urls[0].prefix + this.environment.urls[0].sufix;
            this.environment.api = url;
            this.environment.url_prefix = this.environment.urls[0].prefix + '/';
        }
    }

    /**
     * Specific call to Soledad
     * @param  {string=null} Message id
     * @param  {Object=null} Params
     * @return {Observable<any>} Result of operation
     */
    callSoledad(serviceName: string = null, params: Object = null): Observable<any> {
        let httpParams = new HttpParams();
        if (params) {
            Object.keys(params).forEach((key) => {
                httpParams = httpParams.set(key, params[key]);
            });
        }
        // Call params by serviceName
        if (serviceName !== null) {
            httpParams = httpParams.set('serviceName', serviceName);
            httpParams = httpParams.set('message', 'execute');
            httpParams = httpParams.set('authToken', this.getAuthToken());
        } else {
            httpParams = httpParams.set('message', 'authenticate');
        }

        // Call
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded',
                Accept: 'text/plain',
            }),
            withCredentials: this.environment.production,
        };
        if (serviceName != null && this.checkCache(serviceName)) {
            const json = this.getCache(serviceName, httpParams);
            // if (serviceName == 'b2bNew.dbo.HTML_PortadaLeer') {
            // }
            if (json != null) {
                return of(json).pipe(delay(1));
            }
        }

        if (!this.checkLocalAndSessionToken(serviceName, this.getAuthToken())) {
            return of(undefined);
        }

        // console.log('[Api.Service] Cookies', document.cookie);
        return this.post(this.environment.api, httpParams, options).pipe(
            map((res) => {
                this.checkTokenExpiration(res);

                if (serviceName != null && this.checkCache(serviceName)) {
                    this.saveCache(serviceName, httpParams, res);
                }

                // IMPORTANT: Do not remove. Manage different type of parsing in service balancer
                if (res.value.Rows && res.value) {
                    res.value = res.value.Rows;
                }

                return res;
            })
        );
    }

    private getAuthToken(): string | null {
        return sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
    }

    getConfig(serviceName: string) {
        const name = serviceName.replace(/\./gi, '_');
        const config = servicesConfig[name];
        return config ? config : null;
    }

    checkCache(serviceName): boolean {
        const config = this.getConfig(serviceName);
        if (config && config.cacheable) {
            return true;
        } else {
            return false;
        }
    }

    getCacheName(serviceName: string, httpParams: HttpParams) {
        const email = this.clienteCodigo;
        let key = email + '_' + serviceName;
        key += '_' + sha1(httpParams.toString());
        return key;
    }

    /**
     * Get cache TTL for a service
     * @param serviceName Service name
     * @returns TTL in seconds, or 0 if not configured
     */
    private getCacheTTL(serviceName: string): number {
        const config = this.getConfig(serviceName);
        const ttl = config?.cacheTTL || this.environment.cacheTTL;
        return ttl > 0 ? ttl : 0;
    }

    saveCache(serviceName: string, httpParams: HttpParams, json: any) {
        if (!this.clienteCodigo || !this.environment.useCache) {
            return;
        }
        const cacheKey = this.getCacheName(serviceName, httpParams);

        this.cache.set(cacheKey, {
            data: json,
            timestamp: Date.now(),
            ttl: this.getCacheTTL(serviceName),
        });
    }

    getCache(serviceName: string, httpParams: HttpParams) {
        if (!this.environment.useCache) {
            return null;
        }

        const cacheKey = this.getCacheName(serviceName, httpParams);
        const cachedItem = this.cache.get(cacheKey);

        if (!cachedItem) {
            return null;
        }

        // TTL control
        const ttl = this.getCacheTTL(serviceName);

        if (ttl > 0) {
            const curSeconds = Date.now() / 1000;
            const itemExpiration = cachedItem.timestamp / 1000 + cachedItem.ttl;

            if (curSeconds > itemExpiration) {
                // Remove expired item from cache
                this.cache.delete(cacheKey);
                return null;
            }
        }

        return cachedItem.data;
    }

    navigateLogin(): void {
        // Redirect
        this.store.dispatch(
            new RouterActions.Go({
                path: ['/login'],
            })
        );
    }

    removeTokens(): void {
        // Logout
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('authTokenExtras');
        localStorage.removeItem('authToken');
        localStorage.removeItem('authTokenExtras');
    }

    doLogout(): void {
        console.log('redireccion por token expired');
        this.removeTokens();
        this.navigateLogin();
    }

    checkTokenExpiration(res): void {
        // Token ttl:86400 Segundos
        if (res && res.result === -2) {
            this.doLogout();
        }
    }

    checkLocalAndSessionToken(serviceName: string, token: string): boolean {
        let check =
            serviceName !== null &&
            serviceName !== 'b2bNew.dbo.HTML_UsuariosSolicitudRestaurarContrasena' &&
            serviceName !== 'b2bNew.dbo.HTML_EmailNuevosClientes' &&
            serviceName !== 'b2bNew.dbo.HTML_UsuariosConfirmacionRestaurarContrasena';

        if (check && !token) {
            console.log('redireccion por falta de token [' + serviceName + ']');
            this.navigateLogin();
            return false;
        }
        return true;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
